/**
 * South African ID Number Validation and Auto-fill Utility
 * 
 * SA ID Format: YYMMDDSSSSCAZ (13 digits)
 * - YY: Year of birth (00-99)
 * - MM: Month of birth (01-12) 
 * - DD: Day of birth (01-31)
 * - SSSS: Sequence number (0000-4999 = Female, 5000-9999 = Male)
 * - C: Citizenship (0 = SA Citizen, 1 = Permanent Resident)
 * - A: Usually 8 (race classification - obsolete but still used)
 * - Z: Checksum digit (Luhn algorithm)
 */

/**
 * Validates a South African ID number
 * @param {string} idNumber - The ID number to validate
 * @returns {object} - Validation result with isValid, errors, and extracted data
 */
export const validateSAIdNumber = (idNumber) => {
  const result = {
    isValid: false,
    errors: [],
    data: null
  };

  // Remove any spaces or special characters
  const cleanId = idNumber.replace(/\s/g, '');

  // Check if it's 13 digits
  if (!/^\d{13}$/.test(cleanId)) {
    result.errors.push('ID number must be exactly 13 digits');
    return result;
  }

  // Extract components
  const year = cleanId.substring(0, 2);
  const month = cleanId.substring(2, 4);
  const day = cleanId.substring(4, 6);
  const sequence = cleanId.substring(6, 10);
  const citizenship = cleanId.substring(10, 11);
  const race = cleanId.substring(11, 12);
  const checksum = cleanId.substring(12, 13);

  // Validate date components
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);

  if (monthNum < 1 || monthNum > 12) {
    result.errors.push('Invalid month in ID number');
  }

  if (dayNum < 1 || dayNum > 31) {
    result.errors.push('Invalid day in ID number');
  }

  // Validate citizenship digit
  if (citizenship !== '0' && citizenship !== '1') {
    result.errors.push('Invalid citizenship digit (must be 0 or 1)');
  }

  // Validate checksum using Luhn algorithm
  if (!validateLuhnChecksum(cleanId)) {
    result.errors.push('Invalid ID number checksum');
  }

  // If no errors, extract data
  if (result.errors.length === 0) {
    result.isValid = true;
    result.data = extractIdData(cleanId);
  }

  return result;
};

/**
 * Validates the checksum using the Luhn algorithm (SA ID specific implementation)
 * @param {string} idNumber - The 13-digit ID number
 * @returns {boolean} - True if checksum is valid
 */
const validateLuhnChecksum = (idNumber) => {
  const digits = idNumber.split('').map(Number);
  const checkDigit = digits[12];

  // Calculate checksum for first 12 digits
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    let digit = digits[i];

    // Double every second digit from the left (odd positions when counting from 0)
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) {
        digit = Math.floor(digit / 10) + (digit % 10);
      }
    }

    sum += digit;
  }

  // Calculate check digit
  const calculatedCheckDigit = (10 - (sum % 10)) % 10;

  return calculatedCheckDigit === checkDigit;
};

/**
 * Extracts data from a valid SA ID number
 * @param {string} idNumber - Valid 13-digit ID number
 * @returns {object} - Extracted data (date of birth, gender, citizenship)
 */
const extractIdData = (idNumber) => {
  const year = parseInt(idNumber.substring(0, 2), 10);
  const month = parseInt(idNumber.substring(2, 4), 10);
  const day = parseInt(idNumber.substring(4, 6), 10);
  const sequence = parseInt(idNumber.substring(6, 10), 10);
  const citizenship = idNumber.substring(10, 11);

  // Determine full year (assume cutoff at 30 for current century)
  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const fullYear = year <= 30 ? currentCentury + year : currentCentury - 100 + year;

  // Determine gender from sequence number
  const gender = sequence < 5000 ? 'female' : 'male';

  // Determine citizenship status
  const citizenshipStatus = citizenship === '0' ? 'citizen' : 'permanent_resident';

  // Create date of birth
  const dateOfBirth = `${fullYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

  return {
    dateOfBirth,
    gender,
    citizenship: citizenshipStatus,
    year: fullYear,
    month,
    day,
    sequence,
    isCitizen: citizenship === '0'
  };
};

/**
 * Auto-fills date of birth and gender from SA ID number
 * @param {string} idNumber - The ID number to extract from
 * @returns {object} - Object with dateOfBirth and gender, or null if invalid
 */
export const autoFillFromSAId = (idNumber) => {
  const validation = validateSAIdNumber(idNumber);
  
  if (!validation.isValid) {
    return null;
  }

  return {
    date_of_birth: validation.data.dateOfBirth,
    gender: validation.data.gender
  };
};

/**
 * Formats an ID number with spaces for display
 * @param {string} idNumber - The ID number to format
 * @returns {string} - Formatted ID number (YYMMDD SSSS CAZ)
 */
export const formatIdNumber = (idNumber) => {
  const cleanId = idNumber.replace(/\s/g, '');
  if (cleanId.length !== 13) return idNumber;
  
  return `${cleanId.substring(0, 6)} ${cleanId.substring(6, 10)} ${cleanId.substring(10, 13)}`;
};

/**
 * Checks if ID number is required for course application
 * @param {object} user - User object
 * @returns {boolean} - True if ID is required for applications
 */
export const isIdRequiredForApplication = (user) => {
  // ID number is required for course applications but not for profile saving
  return !user?.id_number || user.id_number.trim() === '';
};

/**
 * Gets user-friendly error messages for ID validation
 * @param {array} errors - Array of error messages from validation
 * @returns {string} - User-friendly error message
 */
export const getIdValidationMessage = (errors) => {
  if (errors.length === 0) return '';
  
  if (errors.includes('ID number must be exactly 13 digits')) {
    return 'Please enter a valid 13-digit South African ID number';
  }
  
  if (errors.includes('Invalid month in ID number') || errors.includes('Invalid day in ID number')) {
    return 'The date of birth in your ID number appears to be invalid';
  }
  
  if (errors.includes('Invalid ID number checksum')) {
    return 'This ID number appears to be invalid. Please check and try again';
  }
  
  return 'Please enter a valid South African ID number';
};

/**
 * Validates if user can apply for courses
 * @param {object} user - User object
 * @returns {object} - Validation result for course application
 */
export const validateForCourseApplication = (user) => {
  const result = {
    canApply: true,
    errors: [],
    warnings: []
  };

  // Check if ID number is provided
  if (!user?.id_number || user.id_number.trim() === '') {
    result.canApply = false;
    result.errors.push('A valid South African ID number is required to apply for courses');
    return result;
  }

  // Validate the ID number
  const validation = validateSAIdNumber(user.id_number);
  if (!validation.isValid) {
    result.canApply = false;
    result.errors.push(getIdValidationMessage(validation.errors));
  }

  // Check other important profile fields for course applications
  const requiredFields = [
    { field: 'first_name', name: 'First Name' },
    { field: 'last_name', name: 'Last Name' },
    { field: 'phone_number', name: 'Phone Number' },
    { field: 'address', name: 'Street Address' },
    { field: 'city', name: 'City' },
    { field: 'postal_code', name: 'Postal Code' }
  ];

  const missingFields = requiredFields.filter(({ field }) =>
    !user[field] || user[field].toString().trim() === ''
  );

  if (missingFields.length > 0) {
    result.warnings.push(
      `Consider completing these profile fields for better application processing: ${
        missingFields.map(f => f.name).join(', ')
      }`
    );
  }

  return result;
};
